import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthRoleGuard } from './guards/auth-role.guard';

import { AdminComponent } from './admin/admin.component';
import { AdminUsersComponent } from './admin-users/admin-users.component';
import { NoAccessComponent } from './no-access/no-access.component';
import { MyaccountComponent } from './myaccount/myaccount.component';
import { ContactusComponent } from './contactus/contactus.component';
import { PrivacypolicyComponent } from './privacypolicy/privacypolicy.component';
import { AboutusComponent } from './aboutus/aboutus.component';
import { DistributorGridComponent } from './distributor-grid/distributor-grid.component';
import { DistributorFormComponent } from './distributor-form/distributor-form.component';
import { MaterialsGridComponent } from './materials-grid/materials-grid.component';
import { MaterialFormComponent } from './material-form/material-form.component';
import { HomeComponent } from './home/home.component';
import { EquipmentGridComponent } from './equipment-grid/equipment-grid.component';
import { EquipmentAddComponent } from './equipment-add/equipment-add.component';
import { EquipmentEditComponent } from './equipment-edit/equipment-edit.component';
import { ManufacturerGridComponent } from './manufacturer-grid/manufacturer-grid.component';
import { ManufacturerAddComponent } from './manufacturer-add/manufacturer-add.component';
import { ManufacturerEditComponent } from './manufacturer-edit/manufacturer-edit.component';
import { ContractorGridComponent } from './contractor/contractor-grid/contractor-grid.component';
import { ContractorAddComponent } from './contractor/contractor-add/contractor-add.component';
import { ContractorEditComponent } from './contractor/contractor-edit/contractor-edit.component';
import { ProjectBidAddComponent } from './project-bid-add/project-bid-add.component';
import { ProjectBidEditComponent } from './project-bid-edit/project-bid-edit.component';
import { ProjectBidGridComponent } from './project-bid-grid/project-bid-grid.component';
import { ProjectGridComponent } from './project/project-grid/project-grid.component';
import { ProjectAddComponent } from './project/project-add/project-add.component';
import { ProjectEditComponent } from './project/project-edit/project-edit.component';
import { ButtonsComponent } from './buttons/buttons.component';
import { QuestionareListComponent } from './questionare-list/questionare-list.component';
import { AddQuestionareComponent } from './add-questionare/add-questionare.component';
import { SmartsheetComponent } from './smartsheet/smartsheet.component';
import { ProviderDashboardComponent } from './components/provider-dashboard/provider-dashboard.component';
import { AuthComponent } from './components/auth/auth.component';
import { ProviderServicesComponent } from './components/provider-services/provider-services.component';
import { CustomerDashboardComponent } from './components/customer-dashboard/customer-dashboard.component';
import { ProjectScheduleComponent } from './project-schedule/project-schedule.component';
import { BrowseServicesComponent } from './browse-services/browse-services.component';
import { EquipmentRentalsComponent } from './equipment-rentals/equipment-rentals.component';
import { PaymentsComponent } from './components/payments/payments.component';
import { CheckoutComponent } from './components/checkout/checkout.component';
import { ServiceHistoryComponent } from './components/service-history/service-history.component';
import { CollectPaymentComponent } from './components/collect-payment/collect-payment.component';
import { ContractorPayoutsComponent } from './components/contractor-payouts/contractor-payouts.component';
import { BecomeProviderComponent } from './become-provider/become-provider.component';
import { EditAccountComponent } from './components/edit-account/edit-account.component';
import { DesignListComponent } from './design-list/design-list.component';
import { DesignEditorComponent } from './design-editor/design-editor.component';
import { AgentCatalogComponent } from './agent-catalog/agent-catalog.component';
import { ResourcePlanningComponent } from './resource-planning/resource-planning.component';
import { PlanDetailComponent } from './plan-detail/plan-detail.component';
import { GetBidComponent } from './get-bid/get-bid.component';
import { AdminProjectTypesComponent } from './admin-project-types/admin-project-types.component';

const routes: Routes = [
  // ── Public ────────────────────────────────────────────────────────────
  { path: '',            component: HomeComponent,
    data: { title: 'Home Services Marketplace', description: 'Already Done connects homeowners with vetted electricians, plumbers, carpenters, and general contractors. Browse services, request a bid, and book trusted local pros.' } },
  { path: 'home',        component: HomeComponent,
    data: { title: 'Home Services Marketplace', description: 'Already Done connects homeowners with vetted electricians, plumbers, carpenters, and general contractors. Browse services, request a bid, and book trusted local pros.' } },
  { path: 'auth',        component: AuthComponent,
    data: { title: 'Sign In or Register', description: 'Sign in or create an account to book trade services, manage projects, or offer your services as a provider on Already Done.' } },
  { path: 'plans/:id',   component: PlanDetailComponent },
  { path: 'plans/:id/checkout', component: CheckoutComponent, canActivate: [AuthRoleGuard],
    data: { title: 'Checkout', signup: true } },
  { path: 'get-a-bid',   component: GetBidComponent,
    data: { title: 'Get a Free Bid', description: 'Tell us about your project and get connected with trusted local contractors ready to bid on your job.' } },
  { path: 'browse-services', component: BrowseServicesComponent,
    data: { title: 'Browse Services', description: 'Search and compare trade services from vetted local contractors — electrical, plumbing, carpentry, HVAC, roofing, and more.' } },
  { path: 'equipment-rentals', component: EquipmentRentalsComponent,
    data: { title: 'Equipment Rentals', description: 'Rent construction and trade equipment — excavators, generators, scaffolding, and more — for your next project.' } },
  { path: 'contactus',  component: ContactusComponent,
    data: { title: 'Contact Us', description: 'Get in touch with the Already Done team with questions about booking services or becoming a provider.' } },
  { path: 'privacy',    component: PrivacypolicyComponent,
    data: { title: 'Privacy Policy' } },
  { path: 'buttons',    component: ButtonsComponent },
  { path: 'smartsheet', component: SmartsheetComponent },

  // ── Become a provider (requires login, any role) ─────────────────────
  { path: 'become-provider', component: BecomeProviderComponent,
    canActivate: [AuthRoleGuard] },

  // ── Provider routes ───────────────────────────────────────────────────
  { path: 'provider-dashboard', component: ProviderDashboardComponent,
    canActivate: [AuthRoleGuard], data: { role: 'provider' } },
  { path: 'provider-services',  component: ProviderServicesComponent,
    canActivate: [AuthRoleGuard], data: { role: 'provider' } },
  { path: 'collect-payment',    component: CollectPaymentComponent,
    canActivate: [AuthRoleGuard], data: { role: 'provider' } },
  { path: 'contractor-payouts', component: ContractorPayoutsComponent,
    canActivate: [AuthRoleGuard], data: { role: 'provider' } },

  // ── Customer routes ───────────────────────────────────────────────────
  { path: 'dashboard',       component: CustomerDashboardComponent,
    canActivate: [AuthRoleGuard], data: { role: 'customer' } },
  { path: 'service-history', component: ServiceHistoryComponent,
    canActivate: [AuthRoleGuard], data: { role: 'customer' } },
  { path: 'payments',        component: PaymentsComponent,
    canActivate: [AuthRoleGuard], data: { title: 'Billing & Payments' } },

  // ── Account ──────────────────────────────────────────────────────────
  { path: 'edit-account', component: EditAccountComponent,
    canActivate: [AuthRoleGuard] },

  // ── Designs ───────────────────────────────────────────────────────────
  { path: 'designs',     component: DesignListComponent,   canActivate: [AuthRoleGuard] },
  { path: 'designs/new', component: DesignEditorComponent, canActivate: [AuthRoleGuard] },
  { path: 'designs/:id', component: DesignEditorComponent, canActivate: [AuthRoleGuard] },
  { path: 'agents',      component: AgentCatalogComponent },
  { path: 'resources',   component: ResourcePlanningComponent, canActivate: [AuthRoleGuard], data: { role: 'provider' } },

  // ── Shared / logged-in ────────────────────────────────────────────────
  { path: 'projects',            component: ProjectGridComponent,     canActivate: [AuthRoleGuard] },
  { path: 'projects/add',        component: ProjectAddComponent,      canActivate: [AuthRoleGuard] },
  { path: 'projects/edit/:id',   component: ProjectEditComponent,     canActivate: [AuthRoleGuard] },
  { path: 'project-bids',        component: ProjectBidGridComponent,  canActivate: [AuthRoleGuard] },
  { path: 'project-bids/add',    component: ProjectBidAddComponent,   canActivate: [AuthRoleGuard] },
  { path: 'project-bids/edit/:id', component: ProjectBidEditComponent, canActivate: [AuthRoleGuard] },
  { path: 'project-schedule',    component: ProjectScheduleComponent, canActivate: [AuthRoleGuard] },

  // ── Admin ─────────────────────────────────────────────────────────────
  { path: 'admin',                component: AdminComponent },
  { path: 'admin/users',          component: AdminUsersComponent },
  { path: 'admin/project-types',  component: AdminProjectTypesComponent },

  // ── Support pages ─────────────────────────────────────────────────────
  { path: 'questionnaire',    component: QuestionareListComponent },
  { path: 'add-questionare',  component: AddQuestionareComponent },
  { path: 'distributor-grid', component: DistributorGridComponent },
  { path: 'addeditdistributor',     component: DistributorFormComponent },
  { path: 'addeditdistributor/:id', component: DistributorFormComponent },
  { path: 'materials',        component: MaterialsGridComponent },
  { path: 'materials/add',    component: MaterialFormComponent },
  { path: 'materials/edit/:id', component: MaterialFormComponent },
  { path: 'equipment',        component: EquipmentGridComponent },
  { path: 'equipment/add',    component: EquipmentAddComponent },
  { path: 'equipment/edit/:id', component: EquipmentEditComponent },
  { path: 'manufacturers',        component: ManufacturerGridComponent },
  { path: 'manufacturers/add',    component: ManufacturerAddComponent },
  { path: 'manufacturers/edit/:id', component: ManufacturerEditComponent },
  { path: 'contractors',          component: ContractorGridComponent,  canActivate: [AuthRoleGuard], data: { role: 'provider' } },
  { path: 'contractors/add',      component: ContractorAddComponent,   canActivate: [AuthRoleGuard], data: { role: 'provider' } },
  { path: 'contractors/edit/:id', component: ContractorEditComponent,  canActivate: [AuthRoleGuard], data: { role: 'provider' } },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true, onSameUrlNavigation: 'reload' })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
